import { MedplumClient } from "@medplum/core";

import { joinReferenceString } from "@/utils/medplum";

import { PREGNANCY_RECORD_CODING_QUERY } from "../constants/record";

// --- Fetch -------------------------------------------------------------------

/**
 * Fetches the most recently updated pregnancy `Condition` linked to a pregnancy `List`.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecordId - FHIR `List` resource ID representing the pregnancy record
 * @returns The most recent pregnancy `Condition`, or `undefined` if none is found
 */
export async function fetchPregnancyConditionForPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecordId: string,
) {
  return medplum.searchOne("Condition", {
    "_has:List:item:_id": pregnancyRecordId,
    code: PREGNANCY_RECORD_CODING_QUERY,
    _sort: "-_lastUpdated",
  });
}

/**
 * Fetches the active pregnancy `Condition` for a patient.
 *
 * @param medplum - Medplum client
 * @param patientId - The patient ID
 * @returns The active pregnancy `Condition`, or `undefined` if none is found
 */
export async function fetchActivePregnancyCondition(medplum: MedplumClient, patientId: string) {
  return medplum.searchOne("Condition", {
    subject: joinReferenceString("Patient", patientId),
    code: PREGNANCY_RECORD_CODING_QUERY,
    "clinical-status": "active",
    _sort: "-_lastUpdated",
  });
}

/**
 * Checks if a patient has an active pregnancy by looking for a pregnancy Condition
 * with clinicalStatus = "active".
 *
 * @param medplum - Medplum client
 * @param patientId - The patient ID to check
 * @returns Promise<boolean> - True if the patient has an active pregnancy Condition, false otherwise
 */
export async function isPatientPregnant(
  medplum: MedplumClient,
  patientId: string,
): Promise<boolean> {
  const condition = await fetchActivePregnancyCondition(medplum, patientId);
  return condition !== undefined;
}
